import { Controller } from "@hotwired/stimulus"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`. Only
// _form4311 has a month select, so only it sends monthPrompt.
const DEFAULT_TRANSLATIONS = {
  monthPrompt: "",
  requiredMessage: "This field is required",
  invalidYearMessage: "Please enter a 4-digit year",
  invalidPhoneMessage: "Please enter a valid phone number",
  invalidAmountMessage: "Please enter a valid amount"
}

export default class extends Controller {
  static targets = ["yearInput", "monthSelect", "form4311Card", "form4313Card", "form4311Tab", "form4313Tab"]
  static values = {
    translations: { type: Object, default: {} },
    months: Array
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.updateMonthOptions()
    this.setupFormValidation()
    this.setupTabListeners()
  }

  selectForm4311(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasForm4311TabTarget) {
      this.form4311TabTarget.click()
    }
    this.updateCardStyles("form4311")
  }

  selectForm4313(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasForm4313TabTarget) {
      this.form4313TabTarget.click()
    }
    this.updateCardStyles("form4313")
  }

  updateCardStyles(activeForm) {
    if (!this.hasForm4311CardTarget || !this.hasForm4313CardTarget) return

    // CSS class names for card states
    const primaryActiveClass = "dividend-form-card--primary-active"
    const successActiveClass = "dividend-form-card--success-active"
    const inactiveClass = "dividend-form-card--inactive"

    if (activeForm === "form4311") {
      this.form4311CardTarget.classList.remove(inactiveClass)
      this.form4311CardTarget.classList.add(primaryActiveClass)
      this.form4313CardTarget.classList.remove(successActiveClass)
      this.form4313CardTarget.classList.add(inactiveClass)
    } else {
      this.form4311CardTarget.classList.remove(primaryActiveClass)
      this.form4311CardTarget.classList.add(inactiveClass)
      this.form4313CardTarget.classList.remove(inactiveClass)
      this.form4313CardTarget.classList.add(successActiveClass)
    }
  }

  setupTabListeners() {
    const tabButtons = this.element.querySelectorAll('[data-bs-toggle="tab"]')
    tabButtons.forEach((tab) => {
      tab.addEventListener("shown.bs.tab", (event) => {
        if (event.target.id === "form4311-tab") {
          this.updateCardStyles("form4311")
        } else {
          this.updateCardStyles("form4313")
        }
      })
    })
  }

  updateMonthOptions() {
    if (!this.hasYearInputTarget || !this.hasMonthSelectTarget) return

    const selectedYear = parseInt(this.yearInputTarget.value, 10)
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1

    const visibleMonths =
      selectedYear < currentYear
        ? this.monthsValue
        : selectedYear === currentYear
          ? this.monthsValue.filter((m) => m.value < currentMonth)
          : []

    // Clear existing options and build new ones safely
    this.monthSelectTarget.innerHTML = ""

    const promptOption = document.createElement("option")
    promptOption.value = ""
    promptOption.textContent = this.#texts.monthPrompt
    this.monthSelectTarget.appendChild(promptOption)

    visibleMonths.forEach((m) => {
      const option = document.createElement("option")
      option.value = m.value
      option.textContent = m.name
      this.monthSelectTarget.appendChild(option)
    })
  }

  setupFormValidation() {
    const form = this.element.closest("form") || this.element.querySelector("form")
    if (!form) return

    form.addEventListener("submit", (event) => {
      this.clearAllErrors()
      if (!this.validateForm(form)) {
        event.preventDefault()
      }
    })

    // Clear error when user starts typing
    form.querySelectorAll("input, select, textarea").forEach((field) => {
      field.addEventListener("input", () => this.clearFieldError(field))
      field.addEventListener("change", () => this.clearFieldError(field))
    })
  }

  validateForm(form) {
    let isValid = true
    const fields = form.querySelectorAll("input[required], select[required], textarea[required]")

    fields.forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false
      }
    })

    return isValid
  }

  validateField(field) {
    const value = field.value.trim()
    const container = field.closest(".lc-input")

    // Check required
    if (field.required && !value) {
      this.showError(container, field, this.#texts.requiredMessage)
      return false
    }

    // Check pattern
    if (field.pattern && value) {
      const regex = new RegExp(`^${field.pattern}$`)
      if (!regex.test(value)) {
        const message = this.getPatternMessage(field)
        this.showError(container, field, message)
        return false
      }
    }

    // Check validity (for number fields, etc.)
    if (!field.validity.valid) {
      const message = field.title || this.getValidityMessage(field)
      this.showError(container, field, message)
      return false
    }

    return true
  }

  getPatternMessage(field) {
    const name = field.name.toLowerCase()
    if (name.includes("year")) {
      return this.#texts.invalidYearMessage
    } else if (name.includes("telefon") || name.includes("phone")) {
      return this.#texts.invalidPhoneMessage
    } else if (name.includes("vat") || name.includes("suma")) {
      return this.#texts.invalidAmountMessage
    }
    return field.title || "Invalid format"
  }

  getValidityMessage(field) {
    if (field.validity.valueMissing) return this.#texts.requiredMessage
    if (field.validity.patternMismatch) return this.getPatternMessage(field)
    if (field.validity.rangeOverflow || field.validity.rangeUnderflow) return this.#texts.invalidAmountMessage
    return "Invalid value"
  }

  showError(container, field, message) {
    if (!container) return

    // Add error styling to the field
    field.classList.add("is-invalid")

    // Check if error element already exists
    let errorEl = container.querySelector(".form-hint.text-danger")
    if (!errorEl) {
      errorEl = document.createElement("div")
      errorEl.className = "form-hint text-danger d-flex align-items-center"

      const iconSpan = document.createElement("span")
      iconSpan.className = "material-symbols-outlined fs-6 me-1"
      iconSpan.textContent = "error"

      const messageSpan = document.createElement("span")
      messageSpan.className = "error-message"

      errorEl.appendChild(iconSpan)
      errorEl.appendChild(messageSpan)
      container.appendChild(errorEl)
    }

    errorEl.querySelector(".error-message").textContent = message
  }

  clearFieldError(field) {
    field.classList.remove("is-invalid")
    const container = field.closest(".lc-input")
    if (container) {
      const errorEl = container.querySelector(".form-hint.text-danger")
      if (errorEl) {
        errorEl.remove()
      }
    }
  }

  clearAllErrors() {
    this.element.querySelectorAll(".is-invalid").forEach((field) => {
      field.classList.remove("is-invalid")
    })
    this.element.querySelectorAll(".form-hint.text-danger").forEach((el) => {
      el.remove()
    })
  }
}
